<template>
  <div v-if="pictures.length > 1">
    <div class="photo-viewer-main">
      <swiper-container ref="wrapperRef" init="false">
        <swiper-slide v-for="(picture, index) in pictures" :key="index" :data-swiper-slide-index="index">
          <div class="photo-viewer-image-wrap">
            <img :src="getImageUrlToFillSquare(picture.path, picture.width, picture.height, 640)" :alt="picture.alt" :loading="index === 0 ? 'eager' : 'lazy'" @click="isShowModal = true" />
            <div v-if="picture.comment" class="photo-viewer-comment">{{ picture.comment }}</div>
          </div>
        </swiper-slide>
      </swiper-container>
      <div :class="$style.photoPager">{{ `${currentIndex + 1} / ${pictures.length}` }}</div>
    </div>
    <ul v-if="hasThumbnail" ref="thumbRef" class="photo-viewer-thumbnail">
      <li v-for="(picture, index) in pictures" :key="index" :class="index === currentIndex ? 'is-current' : ''" @click="onClickThumbnail(index)">
        <div class="photo">
          <img :src="getImageUrlToFillSquare(picture.path, picture.width, picture.height, 120)" :alt="picture.alt" width="40" height="40" />
        </div>
      </li>
    </ul>
    <IModal v-model="isShowModal" :destroy-on-close="true" :fullscreen="true">
      <PhotoDialog :pictures="pictures" :current-index="currentIndex" :is-show-modal="isShowModal" :inject-styles="injectStyles" @change="onChangeDialogSlide" />
    </IModal>
  </div>
  <div v-else>
    <div class="photo-viewer-image-wrap">
      <img :src="getImageUrlToFillSquare(pictures[0].path, pictures[0].width, pictures[0].height, 640)" :alt="pictures[0].alt" @click="isShowModal = true" />
      <div v-if="pictures[0].comment" class="photo-viewer-comment">{{ pictures[0].comment }}</div>
    </div>
    <IModal v-model="isShowModal" :destroy-on-close="true" :fullscreen="true">
      <PhotoDialog :pictures="pictures" :is-show-modal="isShowModal" />
    </IModal>
  </div>
</template>

<script setup lang="ts">
import type Swiper from 'swiper';
import { register } from 'swiper/element/bundle';
import { ref, onMounted } from 'vue';
import { dynamicImageUrl } from '@/js/lib/helper/url';
import IModal from '@/js/components/molecules/IModal.vue';
import PhotoDialog from './PhotoDialog.vue';

const props = defineProps<{
  pictures: {
    path: string;
    alt: string;
    width: number;
    height: number;
    comment: string;
  }[];
  hasThumbnail?: boolean;
}>();
const injectStyles = [
  `
  :host .swiper-button-prev,
  :host .swiper-button-next {
    color: var(--color-white);
    background: none rgba(0, 0, 0, 0.4);
    border-radius: 50%;
    height: 38px;
    width: 38px;
  }
  :host .swiper-button-prev:hover,
  :host .swiper-button-next:hover {
    opacity: 0.7;
  }
  :host .swiper-button-disabled {
    opacity: 0.25;
    pointer-events: initial;
  }
  :host .swiper-button-prev > svg {
    height: 50%;
    margin-right: 3px;
  }
  :host .swiper-button-next > svg {
    height: 50%;
    margin-left: 3px;
  }
  `,
];
const wrapperRef = ref();
const thumbRef = ref<HTMLUListElement>();
const isShowModal = ref(false);
const swiper = ref<Swiper>();
const currentIndex = ref(0);
const getImageUrlToFillSquare = (path: string, width: number, height: number, squareSize: number) => {
  let w: number | undefined;
  let h: number | undefined;

  if (width > height) {
    h = squareSize;
  } else {
    w = squareSize;
  }

  return dynamicImageUrl(path, w, h);
};
const onClickThumbnail = (index: number) => {
  swiper.value?.slideToLoop(index);
};
const onChangeDialogSlide = (index: number) => {
  swiper.value?.slideToLoop(index);
};

// register Swiper custom elements
register();

onMounted(() => {
  if (props.pictures.length > 1) {
    Object.assign(wrapperRef.value, {
      loop: true,
      navigation: true,
      injectStyles,
    });

    wrapperRef.value.addEventListener('swiperprogress', (e: { detail: [Swiper, number] }) => {
      [swiper.value] = e.detail;
    });
    wrapperRef.value.addEventListener('swiperslidechange', () => {
      if (currentIndex.value !== swiper.value?.realIndex) {
        currentIndex.value = swiper.value?.realIndex ?? 0;
        thumbRef.value?.children[currentIndex.value]?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
      }
    });
    wrapperRef.value.initialize();
  }
});
</script>

<style module lang="scss">
.photoPager {
  background-color: var(--color-black-alpha40);
  border-radius: 20px;
  color: var(--color-white);
  font-size: 12px;
  padding: 2px 10px;
  position: absolute;
  right: 10px;
  top: 10px;
  z-index: 1;
}
</style>
